import { makeStyles } from '@material-ui/core/styles';
import typography from '@/theme/typography';
import {
  TextColors,
  SecondaryColors,
  TextOpacities,
  SpacingSizes,
  spacingSizesMap,
} from '@/utils/constants';
import { hexToRGBA } from '@/utils/helpers';

const darkTheme = {
  root: {
    color: TextColors.light,

    '& $heading': {
      color: TextColors.light,
    },

    '& $listItem, $featured': {
      borderBottomColor: hexToRGBA(TextColors.light, TextOpacities.light4),
    },

    '& $link': {
      '&:not(:hover)': {
        color: TextColors.light,
      },

      '&:hover': {
        color: SecondaryColors.main,
      },
    },

    '& $icon': {
      color: SecondaryColors.main,
    },

    '& $info': {
      color: hexToRGBA(TextColors.light, TextOpacities.light1),
    },
  },
};

const lightTheme = {
  root: {
    color: TextColors.dark,

    '& $heading': {
      color: TextColors.dark,
    },

    '& $listItem, $featured': {
      borderBottomColor: hexToRGBA(TextColors.dark, TextOpacities.dark4),
    },

    '& $link': {
      '&:not(:hover)': {
        color: TextColors.dark,
      },

      '&:hover': {
        color: SecondaryColors.dark,
      },
    },

    '& $icon': {
      color: SecondaryColors.dark,
    },

    '& $info': {
      color: hexToRGBA(TextColors.dark, TextOpacities.dark1),
    },
  },
};

const getTopSpacing = (muiTheme: any) => ({
  '&.top-none': {
    '& $leftContent': {
      [muiTheme.breakpoints.up('md')]: {
        top: `${spacingSizesMap[SpacingSizes.none] + 58}px`, // 58px from header height
      },
    },
  },
  '&.top-small': {
    '& $leftContent': {
      [muiTheme.breakpoints.up('md')]: {
        top: `${spacingSizesMap[SpacingSizes.small] + 58}px`, // 58px from header height
      },
    },
  },
  '&.top-medium': {
    '& $leftContent': {
      [muiTheme.breakpoints.up('md')]: {
        top: `${spacingSizesMap[SpacingSizes.medium] + 58}px`, // 58px from header height
      },
    },
  },
  '&.top-large': {
    '& $leftContent': {
      [muiTheme.breakpoints.up('md')]: {
        top: `${spacingSizesMap[SpacingSizes.large] + 58}px`, // 58px from header height
      },
    },
  },
});

const useStyles = makeStyles(
  (muiTheme) => ({
    root: {
      '&.dark-theme': darkTheme.root,
      '&.light-theme': lightTheme.root,
      ...getTopSpacing(muiTheme),
    },

    intro: {
      position: 'relative',
      marginBottom: muiTheme.spacing(3),

      [muiTheme.breakpoints.up('md')]: {
        width: 'calc(70% - 40px)',
      },
    },

    heading: {
      '&.MuiTypography-root': {
        marginTop: 0,

        '& *': {
          margin: 0,
        },
      },
    },

    subHeading: {
      marginTop: '0',
    },

    leftContent: {
      [muiTheme.breakpoints.up('md')]: {
        position: 'sticky',
      },
    },

    featured: {
      alignSelf: 'flex-start',
      position: 'relative',
      width: '100%',

      [muiTheme.breakpoints.down('sm')]: {
        paddingBottom: '20px',
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
        marginBottom: muiTheme.spacing(3),
      },
    },

    list: {
      position: 'relative',
    },

    listItem: {
      margin: '20px 0',
      paddingBottom: '20px',
      borderBottomWidth: 1,
      borderBottomStyle: 'solid',
      position: 'relative',

      '& p': {
        marginTop: '0',
      },

      '&:first-child': {
        marginTop: 0,
        paddingTop: '0',
      },

      '& a': {
        ...typography.body1,
      },
    },

    link: {
      display: 'block',
      transition: 'color 150ms ease-in-out',

      '& > *': {
        display: 'inline',
        verticalAlign: 'middle',
      },

      '&:before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
      },

      '&:not(:hover)': {
        '& .MuiSvgIcon-root': {
          opacity: '0',
          transform: 'translateX(-5px)',
        },
      },

      '&:hover': {
        '& .MuiSvgIcon-root': {
          opacity: '1',
          transform: 'translateX(0px)',
        },
      },
    },

    featureLink: {
      marginBottom: muiTheme.spacing(1.5),
    },

    featureDescription: {
      display: 'inline',
    },

    icon: {
      display: 'inline !important',
      fontSize: '1.25em !important',
      marginLeft: '5px !important',
      verticalAlign: 'middle',
      transition: 'all 150ms ease-in-out',
    },

    info: {
      '&.MuiTypography-root': {
        marginBottom: muiTheme.spacing(1.5),
      },
    },

    date: {
      '&:not(:last-child):after': {
        content: '"|"',
        display: 'inline-block',
        padding: '0 10px',
      },
    },

    author: {
      '&:after': {
        content: '"|"',
        display: 'inline-block',
        padding: '0 10px',
      },
    },
    category: {},

    trigger: {
      position: 'absolute',
    },

    image: {
      width: '100%',
      marginBottom: muiTheme.spacing(3),
    },
  }),
  { name: 'GenNews' },
);

export default useStyles;
